/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import {useEffect, useState} from 'react';
import RouteManager from '../routes/RouteManager';

const ROUTE_CHANGE_START    = 'routeChangeStart';
const ROUTE_CHANGE_COMPLETE = 'routeChangeComplete';
const ROUTE_CHANGE_ERROR    = 'routeChangeError';

// decodes the url, keeping it as is when its percent-encoding is malformed
// (decodeURIComponent would throw) ...
const decodeRoute = (url: string): string => {
  let route = url;
  try {
    route = decodeURIComponent(url);
  } catch {
    route = url;
  }
  return route;
};

const getRoutePath = (url?: string): string => (
  decodeRoute(url || '').split(/[?#]/)[0]
);

/**
 * Resolves the page whose component is actually rendered, as opposed to the
 * page the application has already switched its navigation state to.
 *
 * The iOn shell updates its current page as soon as a route change starts,
 * while the Next.js pages router may still be loading the target page's
 * chunk for several seconds. This hook tracks the router lifecycle so
 * navigation chrome (e.g. the current-theme top navigation) can wait for the
 * target page to render:
 *
 * - `routeChangeStart` marks the route change as pending;
 * - `routeChangeComplete` records the completed route as the rendered page
 *   and clears the pending state;
 * - `routeChangeError` (failed or cancelled change) clears the pending state.
 *
 * ```typescript
 * import {useRenderedPage} from '@ion/applications/src/navigation/useRenderedPage';
 * ...
 *
 *   // the previously rendered page while the target page is loading ...
 *   const renderedPage = useRenderedPage(currentPage);
 * ```
 *
 * @param currentPage The page held by the application navigation state.
 * @returns `currentPage` when no route change is pending, otherwise the
 *          path of the last page the router finished rendering.
 */
export const useRenderedPage = (currentPage: string): string => {
  const [routeChangePending, setRouteChangePending] = useState(false);
  const [renderedRoute, setRenderedRoute]           = useState(function() {
    return getRoutePath(RouteManager.getAsPath());
  });

  useEffect(() => {
    const onRouteChangeStart = function() {
      setRouteChangePending(true);
    };
    const onRouteChangeComplete = function(url: string) {
      setRenderedRoute(getRoutePath(url));
      setRouteChangePending(false);
    };
    const onRouteChangeError = function() {
      setRouteChangePending(false);
    };
    RouteManager.addEventListener(ROUTE_CHANGE_START, onRouteChangeStart);
    RouteManager.addEventListener(ROUTE_CHANGE_COMPLETE, onRouteChangeComplete);
    RouteManager.addEventListener(ROUTE_CHANGE_ERROR, onRouteChangeError);
    return function() {
      RouteManager.removeEventListener(ROUTE_CHANGE_START, onRouteChangeStart);
      RouteManager.removeEventListener(ROUTE_CHANGE_COMPLETE, onRouteChangeComplete);
      RouteManager.removeEventListener(ROUTE_CHANGE_ERROR, onRouteChangeError);
    };
  }, []);

  return routeChangePending ? renderedRoute : currentPage;
};

export default useRenderedPage;
